import React, { useEffect, useMemo, useState } from 'react';
import { RotateCw, X, Search, CheckCircle, AlertCircle, Users, Plus, Trash2, ShieldCheck, Info } from 'lucide-react';
import api from '../services/api';

interface ReabonnementModalProps {
    isOpen: boolean;
    mode: 'abonnement' | 'seance';
    /** Client à présélectionner (ex: depuis une ligne de la liste) */
    initialClient?: any | null;
    onClose: () => void;
    /** Appelé après succès avec les données prêtes pour le reçu */
    onSuccess: (receipt: any, client: any) => void;
    /** Prolonger un abonnement encore actif */
    onOpenProlonger?: (sub: any) => void;
}

const PAYMENT_METHODS = [
    { value: 'especes', label: 'Espèces' },
    { value: 'wave', label: 'Wave' },
    { value: 'orange_money', label: 'Orange Money' },
    { value: 'mtn_money', label: 'MTN Money' },
    { value: 'moov_money', label: 'Moov Money' },
    { value: 'carte_bancaire', label: 'Carte Bancaire' },
    { value: 'virement', label: 'Virement' },
];

const today = () => new Date().toISOString().split('T')[0];
const nowTime = () => new Date().toTimeString().substring(0, 5);
const fmt = (d?: string) => (d ? new Date(d).toLocaleDateString('fr-FR') : '-');

const addPeriod = (start: string, periodicity: string, q: number) => {
    const d = new Date(start);
    switch (periodicity) {
        case 'trimestriel': d.setMonth(d.getMonth() + 3 * q); break;
        case 'semestriel': d.setMonth(d.getMonth() + 6 * q); break;
        case 'annuel': d.setFullYear(d.getFullYear() + q); break;
        case 'hebdomadaire': d.setDate(d.getDate() + 7 * q); break;
        default: d.setMonth(d.getMonth() + q);
    }
    return d.toISOString().split('T')[0];
};

const ReabonnementModal: React.FC<ReabonnementModalProps> = ({ isOpen, mode, initialClient, onClose, onSuccess, onOpenProlonger }) => {
    const isAbo = mode === 'abonnement';
    const [clients, setClients] = useState<any[]>([]);
    const [forfaits, setForfaits] = useState<any[]>([]);
    const [coaches, setCoaches] = useState<any[]>([]);
    const [coachId, setCoachId] = useState('');
    const [search, setSearch] = useState('');
    const [client, setClient] = useState<any>(null);
    const [lastSub, setLastSub] = useState<any>(null);
    const [forfaitId, setForfaitId] = useState('');
    const [includeRegistrationFee, setIncludeRegistrationFee] = useState(false);
    const [paymentMethod, setPaymentMethod] = useState('');
    const [startDate, setStartDate] = useState(today());
    const [quantity, setQuantity] = useState(1);
    const [sessionDate, setSessionDate] = useState(today());
    const [sessionTime, setSessionTime] = useState(nowTime());
    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState('');

    // Chargement des clients et forfaits à l'ouverture
    useEffect(() => {
        if (!isOpen) return;
        setError('');
        setSearch('');
        setPaymentMethod('');
        setQuantity(1);
        setCoachId('');
        setStartDate(today());
        setSessionDate(today());
        setSessionTime(nowTime());
        setClient(initialClient || null);
        api.get('/clients').then(r => setClients(r.data)).catch(() => {});
        api.get('/coaches').then(r => setCoaches(r.data || [])).catch(() => {});
        api.get(isAbo ? '/forfaits/abonnements?active_only=true' : '/forfaits/seances?active_only=true')
            .then(r => {
                setForfaits(r.data);
                if (r.data.length > 0 && !forfaitId) {
                    setForfaitId(String(r.data[0].id));
                }
            })
            .catch(() => {});
    }, [isOpen, isAbo, initialClient]);

    // Dernier abonnement du client sélectionné
    useEffect(() => {
        setLastSub(null);
        if (!client || !isAbo) return;
        api.get(`/subscriptions/client/${client.id}`)
            .then(r => {
                const list: any[] = r.data || [];
                if (list.length === 0) return;
                const last = [...list].sort((a, b) => String(b.end_date).localeCompare(String(a.end_date)))[0];
                setLastSub(last);
                // Présélectionner automatiquement le forfait précédent du client
                if (last.forfait_abonnement_id) {
                    setForfaitId(String(last.forfait_abonnement_id));
                }
                // Abonnement encore en cours : le nouveau démarre à la fin de l'actuel
                if (last.end_date && new Date(last.end_date) >= new Date(today())) {
                    setStartDate(String(last.end_date).substring(0, 10));
                }
            })
            .catch(() => {});
    }, [client, isAbo]);

    const suggestions = useMemo(() => {
        const q = search.trim().toLowerCase();
        if (q.length < 2) return [];
        return clients
            .filter(c => `${c.last_name} ${c.first_name}`.toLowerCase().includes(q) || (c.phone && c.phone.includes(q)))
            .slice(0, 6);
    }, [search, clients]);

    const forfait = forfaits.find(f => String(f.id) === forfaitId);
    const isGroupPack = Boolean(
        isAbo && (
            (forfait?.max_members && Number(forfait.max_members) > 1) ||
            forfait?.name?.toLowerCase().includes('pack entreprise')
        )
    );
    const maxAdditionalAllowed = 4; // 1 souscripteur + 4 additionnels = 5 max
    const [additionalPackMembers, setAdditionalPackMembers] = useState<Array<{ full_name: string; phone: string }>>([]);

    // Déterminer si le client conserve le même forfait
    const isSameForfait = useMemo(() => {
        if (!lastSub || !forfaitId) return false;
        if (lastSub.forfait_abonnement_id && String(lastSub.forfait_abonnement_id) === String(forfaitId)) {
            return true;
        }
        if (lastSub.type_name && forfait?.name && lastSub.type_name.toLowerCase().trim() === forfait.name.toLowerCase().trim()) {
            return true;
        }
        return false;
    }, [lastSub, forfaitId, forfait]);

    // Déterminer si l'inscription annuelle du client est encore valide (< 365 jours)
    const isAnnualValid = useMemo(() => {
        const refDate = client?.last_inscription_date || lastSub?.start_date || lastSub?.created_at;
        if (!refDate) return false;
        const ref = new Date(refDate);
        const now = new Date();
        const diffDays = (now.getTime() - ref.getTime()) / (1000 * 60 * 60 * 24);
        return diffDays <= 365;
    }, [client, lastSub]);

    // RÈGLE MÉTIER : Frais d'inscription annuels.
    // Ne s'appliquent PAS à chaque réabonnement, SAUF si le client change de forfait (ou que son inscription > 1 an).
    useEffect(() => {
        if (!forfait || Number(forfait.registration_fee || 0) === 0) {
            setIncludeRegistrationFee(false);
            return;
        }
        if (lastSub) {
            if (isSameForfait && isAnnualValid) {
                // Même forfait et annuel encore valide : Pas de frais d'inscription
                setIncludeRegistrationFee(false);
            } else {
                // Changement de forfait OU annuel expiré : Frais d'inscription appliqués
                setIncludeRegistrationFee(true);
            }
        } else {
            // Premier abonnement sans historique
            setIncludeRegistrationFee(true);
        }
    }, [forfaitId, lastSub, isSameForfait, isAnnualValid]);

    const regFee = includeRegistrationFee && forfait?.registration_fee ? Number(forfait.registration_fee) : 0;
    const basePrice = forfait ? Number(forfait.price) * (isAbo ? Math.max(1, quantity) : 1) : 0;
    const total = basePrice + regFee;
    const endDate = isAbo && forfait && startDate ? addPeriod(startDate, forfait.periodicity, Math.max(1, quantity)) : '';
    const lastExpired = lastSub?.end_date && new Date(lastSub.end_date) < new Date(today());

    if (!isOpen) return null;

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setError('');
        if (!client) return setError('Veuillez sélectionner un client existant');
        if (!forfaitId) return setError('Veuillez sélectionner un forfait');
        if (!paymentMethod) return setError('Veuillez sélectionner un mode de paiement');
        if (forfait?.with_coach && !coachId) {
            return setError('Ce forfait requiert un coach : veuillez sélectionner le coach personnel');
        }
        if (isAbo && lastSub && !lastExpired) {
            return setError("L'abonnement de ce client est encore actif. Le réabonnement n'est disponible que pour les abonnements expirés. Utilisez la prolongation.");
        }

        try {
            setSubmitting(true);
            const label = PAYMENT_METHODS.find(m => m.value === paymentMethod)?.label || paymentMethod;
            const clientName = `${client.first_name} ${client.last_name}`;
            if (isAbo) {
                const cleanMembers = isGroupPack
                    ? [
                        {
                            full_name: `${client.first_name} ${client.last_name}`.trim(),
                            phone: (client.phone || '').trim(),
                            role: 'Souscripteur principal'
                        },
                        ...additionalPackMembers
                            .map(m => ({ full_name: m.full_name.trim(), phone: m.phone.trim() }))
                            .filter(m => m.full_name.length > 0)
                            .slice(0, maxAdditionalAllowed)
                    ]
                    : [];

                await api.post('/subscriptions/renouveler', {
                    client_id: client.id, 
                    forfait_id: Number(forfaitId), 
                    payment_method: paymentMethod,
                    start_date: startDate, 
                    quantity, 
                    include_inscription: includeRegistrationFee,
                    coach_id: coachId ? Number(coachId) : null,
                    group_members: cleanMembers.length > 0 ? cleanMembers : null
                });
                onSuccess({
                    clientName, 
                    clientPhone: client.phone, 
                    forfaitName: forfait?.name || 'Abonnement', 
                    periodicity: forfait?.periodicity || '',
                    duration: forfait?.duration || '', 
                    startDate: fmt(startDate), 
                    endDate: fmt(endDate),
                    quantity, 
                    paymentMethod: label, 
                    total,
                    hasInscription: includeRegistrationFee && regFee > 0
                }, client);
            } else {
                await api.post('/seances/renouveler', {
                    client_id: client.id, 
                    forfait_id: Number(forfaitId), 
                    payment_method: paymentMethod,
                    session_date: sessionDate, 
                    session_time: sessionTime, 
                    coach_id: coachId ? Number(coachId) : null
                });
                onSuccess({
                    clientName, 
                    clientPhone: client.phone, 
                    forfaitName: forfait?.name || 'Séance', 
                    sessionDate: fmt(sessionDate),
                    sessionTime, 
                    paymentMethod: label, 
                    total,
                    hasInscription: false
                }, client);
            }
            onClose();
        } catch (err: any) {
            setError(err.response?.data?.error || 'Erreur lors du réabonnement');
        } finally {
            setSubmitting(false);
        }
    };

    const inputCls = 'w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500';
    const labelCls = 'block text-xs font-bold text-gray-600 uppercase mb-1';

    return (
        <div className="fixed inset-0 z-[70] flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm">
            <form onSubmit={handleSubmit} className="bg-white rounded-2xl shadow-2xl w-full max-w-lg max-h-[94vh] overflow-y-auto">
                <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
                    <h3 className="text-lg font-black text-gray-900 flex items-center gap-2">
                        <RotateCw size={18} className="text-blue-600" />
                        Réabonnement {isAbo ? 'Abonnement' : 'Séance'}
                    </h3>
                    <button type="button" onClick={onClose} className="p-1.5 rounded-full bg-gray-100 text-gray-500 hover:text-gray-800">
                        <X size={18} />
                    </button>
                </div>

                <div className="p-6 space-y-4">
                    {/* Client */}
                    <div>
                        <label className={labelCls}>Client existant</label>
                        {client ? (
                            <div className="flex items-center justify-between bg-blue-50 border border-blue-200 rounded-md px-3 py-2">
                                <div className="text-sm">
                                    <div className="font-bold text-gray-900">{client.last_name} {client.first_name}</div>
                                    <div className="text-xs text-gray-500 font-mono">{client.phone || '-'}</div>
                                </div>
                                <button type="button" onClick={() => { setClient(null); setLastSub(null); }} className="text-xs font-semibold text-blue-700 hover:underline">
                                    Changer
                                </button>
                            </div>
                        ) : (
                            <div className="relative">
                                <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                                <input
                                    autoFocus
                                    value={search}
                                    onChange={e => setSearch(e.target.value)}
                                    placeholder="Rechercher par nom ou téléphone..."
                                    className={`${inputCls} pl-9`}
                                />
                                {suggestions.length > 0 && (
                                    <div className="absolute z-10 mt-1 w-full bg-white border border-gray-200 rounded-md shadow-lg overflow-hidden">
                                        {suggestions.map(c => (
                                             <button type="button" key={c.id} onClick={() => setClient(c)}
                                                className="w-full text-left px-3 py-2 text-sm hover:bg-blue-50 flex justify-between">
                                                <span className="font-semibold">{c.last_name} {c.first_name}</span>
                                                <span className="text-xs text-gray-500 font-mono">{c.phone}</span>
                                            </button>
                                        ))}
                                    </div>
                                )}
                            </div>
                        )}
                    </div>

                    {/* Dernier abonnement */}
                    {isAbo && lastSub && (
                        <div className={`text-xs rounded-xl p-3 border ${lastExpired ? 'bg-red-50 border-red-200 text-red-800' : 'bg-amber-50 border-amber-200 text-amber-900'}`}>
                            <div className="font-semibold mb-1">
                                Dernier abonnement : <strong>{lastSub.type_name || lastSub.forfait_name || 'Abonnement'}</strong> — {lastExpired ? 'expiré le' : 'valide jusqu\'au'} <strong>{fmt(lastSub.end_date)}</strong>
                            </div>
                            {!lastExpired && (
                                <div className="space-y-2 mt-2 pt-2 border-t border-amber-200/80">
                                    <p className="text-amber-800 font-medium">
                                        ⚠️ L'abonnement de ce client est encore <strong>actif</strong>. Le réabonnement n'est disponible que pour un abonnement expiré. Vous devez plutôt utiliser la prolongation pour augmenter sa durée.
                                    </p>
                                    {onOpenProlonger && (
                                        <button
                                            type="button"
                                            onClick={() => {
                                                onClose();
                                                onOpenProlonger(lastSub);
                                            }}
                                            className="px-3 py-1.5 bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg font-bold text-xs shadow-xs transition-colors cursor-pointer"
                                        >
                                            Prolonger cet abonnement en cours
                                        </button>
                                    )}
                                </div>
                            )}
                        </div>
                    )}

                    {/* Forfait */}
                    <div>
                        <label className={labelCls}>Forfait</label>
                        <select value={forfaitId} onChange={e => setForfaitId(e.target.value)} className={inputCls}>
                            {forfaits.map(f => (
                                <option key={f.id} value={f.id}>
                                    {f.name} — {Number(f.price).toLocaleString()} FCFA{f.periodicity ? ` (${f.periodicity})` : ''}
                                </option>
                            ))}
                        </select>
                    </div>

                    {/* Coach personnel si forfait avec coach */}
                    {Boolean(forfait?.with_coach) && (
                        <div className="p-3 bg-amber-50 border border-amber-300 rounded-lg space-y-1">
                            <label className="block text-xs font-bold text-amber-900 uppercase">
                                🏋️ Coach Personnel Attitré <span className="text-red-500">*</span>
                            </label>
                            <select
                                required
                                value={coachId}
                                onChange={e => setCoachId(e.target.value)}
                                className={inputCls}
                            >
                                <option value="">-- Sélectionner le coach --</option>
                                {coaches.map(c => (
                                    <option key={c.id} value={c.id}>
                                        {c.first_name} {c.last_name} {c.specialties ? `(${c.specialties})` : ''}
                                    </option>
                                ))}
                            </select>
                        </div>
                    )}

                    {/* Section membres additionnels si PACK ENTREPRISE */}
                    {isGroupPack && (
                        <div className="p-4 bg-purple-50 border border-purple-200 rounded-xl space-y-3">
                            <div className="flex items-center justify-between">
                                <div className="flex items-center gap-2 text-purple-900">
                                    <Users size={18} />
                                    <span className="font-bold text-xs uppercase">
                                        Membres du Pack ({1 + additionalPackMembers.length}/5 personnes)
                                    </span>
                                </div>
                                {additionalPackMembers.length < maxAdditionalAllowed && (
                                    <button
                                        type="button"
                                        onClick={() => setAdditionalPackMembers(prev => [...prev, { full_name: '', phone: '' }])}
                                        className="text-xs bg-purple-600 hover:bg-purple-700 text-white font-bold px-2.5 py-1 rounded-md flex items-center gap-1 shadow-xs"
                                    >
                                        <Plus size={13} /> Ajouter (+{maxAdditionalAllowed - additionalPackMembers.length})
                                    </button>
                                )}
                            </div>

                            <p className="text-[11px] text-purple-800">
                                Le souscripteur <strong>{client?.first_name} {client?.last_name}</strong> est le membre principal (1/5). Vous pouvez renseigner ci-dessous les autres membres qui partageront le même badge.
                            </p>

                            <div className="space-y-2">
                                <div className="p-2.5 bg-white border border-purple-200 rounded-lg flex items-center justify-between text-xs">
                                    <div>
                                        <span className="font-bold text-purple-900">1. {client?.first_name} {client?.last_name}</span>
                                        <span className="text-gray-500 font-mono text-[11px] ml-2">({client?.phone || 'Souscripteur principal'})</span>
                                    </div>
                                    <span className="text-[10px] uppercase font-bold text-purple-700 bg-purple-100 px-2 py-0.5 rounded">Titulaire</span>
                                </div>

                                {additionalPackMembers.map((member, idx) => (
                                    <div key={idx} className="p-2.5 bg-white border border-purple-200 rounded-lg flex flex-col sm:flex-row gap-2 items-center">
                                        <span className="text-xs font-bold text-purple-700 w-6 shrink-0">{idx + 2}.</span>
                                        <input
                                            type="text"
                                            required
                                            value={member.full_name}
                                            onChange={e => {
                                                const copy = [...additionalPackMembers];
                                                copy[idx].full_name = e.target.value;
                                                setAdditionalPackMembers(copy);
                                            }}
                                            placeholder="Nom & Prénoms"
                                            className="flex-1 px-2.5 py-1.5 border border-gray-300 rounded text-xs focus:ring-1 focus:ring-purple-500"
                                        />
                                        <input
                                            type="tel"
                                            value={member.phone}
                                            onChange={e => {
                                                const copy = [...additionalPackMembers];
                                                copy[idx].phone = e.target.value;
                                                setAdditionalPackMembers(copy);
                                            }}
                                            placeholder="Téléphone"
                                            className="w-full sm:w-32 px-2.5 py-1.5 border border-gray-300 rounded text-xs focus:ring-1 focus:ring-purple-500 font-mono"
                                        />
                                        <button
                                            type="button"
                                            onClick={() => setAdditionalPackMembers(prev => prev.filter((_, i) => i !== idx))}
                                            className="text-red-500 hover:text-red-700 p-1"
                                            title="Retirer"
                                        >
                                            <Trash2 size={15} />
                                        </button>
                                    </div>
                                ))}

                                {additionalPackMembers.length === 0 && (
                                    <div className="text-[11px] text-gray-500 italic p-2 bg-purple-50/50 rounded border border-dashed border-purple-200 text-center">
                                        Aucun membre additionnel ajouté pour le moment.
                                    </div>
                                )}
                            </div>
                            <p className="text-[10px] text-purple-700 italic">
                                1 seul badge partagé sera utilisé pour l'ensemble des membres de ce pack.
                            </p>
                        </div>
                    )}

                    {/* Bloc Frais d'inscription annuels : Ne s'appliquent pas sauf changement de forfait */}
                    {isAbo && forfait && Number(forfait.registration_fee) > 0 && (
                        <div className={`p-3.5 rounded-xl border text-xs space-y-2 transition-all ${
                            !includeRegistrationFee 
                                ? 'bg-emerald-50/90 border-emerald-200 text-emerald-950' 
                                : 'bg-amber-50/90 border-amber-200 text-amber-950'
                        }`}>
                            <div className="flex items-center justify-between gap-2">
                                <div className="flex items-center gap-2">
                                    <input
                                        id="include_reg_fee"
                                        type="checkbox"
                                        checked={includeRegistrationFee}
                                        onChange={e => setIncludeRegistrationFee(e.target.checked)}
                                        className="w-4 h-4 text-brand-primary rounded focus:ring-brand-primary cursor-pointer shrink-0"
                                    />
                                    <label htmlFor="include_reg_fee" className="font-extrabold cursor-pointer select-none">
                                        Frais d'inscription annuels ({Number(forfait.registration_fee).toLocaleString('fr-FR')} FCFA)
                                    </label>
                                </div>
                                <span className={`text-[10px] uppercase font-black px-2 py-0.5 rounded whitespace-nowrap shrink-0 ${
                                    !includeRegistrationFee 
                                        ? 'bg-emerald-200 text-emerald-800' 
                                        : 'bg-amber-200 text-amber-900'
                                }`}>
                                    {!includeRegistrationFee ? '0 FCFA (Non appliqués)' : `+${Number(forfait.registration_fee).toLocaleString('fr-FR')} FCFA Inclus`}
                                </span>
                            </div>

                            <div className="text-[11px] leading-relaxed pl-6">
                                {!includeRegistrationFee ? (
                                    <p className="text-emerald-800 flex items-start gap-1.5">
                                        <ShieldCheck size={14} className="text-emerald-600 shrink-0 mt-0.5" />
                                        <span>
                                            <strong>Cotisation annuelle déjà à jour :</strong> Le client conserve le même forfait (<em>{forfait.name}</em>). Les frais annuels ne sont pas facturés lors du réabonnement.
                                        </span>
                                    </p>
                                ) : isSameForfait ? (
                                    <p className="text-amber-800 flex items-start gap-1.5">
                                        <AlertCircle size={14} className="text-amber-600 shrink-0 mt-0.5" />
                                        <span>
                                            <strong>Renouvellement annuel :</strong> L'inscription annuelle du client est cochée (+10 000 FCFA).
                                        </span>
                                    </p>
                                ) : (
                                    <p className="text-amber-800 flex items-start gap-1.5">
                                        <Info size={14} className="text-amber-600 shrink-0 mt-0.5" />
                                        <span>
                                            <strong>Changement de forfait détecté :</strong> Le client passe à un nouveau forfait (<em>{forfait.name}</em>). Les frais d'inscription du nouveau forfait s'appliquent.
                                        </span>
                                    </p>
                                )}
                            </div>
                        </div>
                    )}

                    {isAbo ? (
                        <div className="grid grid-cols-2 gap-3">
                            <div>
                                <label className={labelCls}>Date de début</label>
                                <input type="date" value={startDate} onChange={e => setStartDate(e.target.value)} className={inputCls} />
                            </div>
                            <div>
                                <label className={labelCls}>Quantité</label>
                                <input type="number" min={1} value={quantity} onChange={e => setQuantity(Math.max(1, Number(e.target.value) || 1))} className={inputCls} />
                            </div>
                        </div>
                    ) : (
                        <div className="grid grid-cols-2 gap-3">
                            <div>
                                <label className={labelCls}>Date</label>
                                <input type="date" value={sessionDate} onChange={e => setSessionDate(e.target.value)} className={inputCls} />
                            </div>
                            <div>
                                <label className={labelCls}>Heure</label>
                                <input type="time" value={sessionTime} onChange={e => setSessionTime(e.target.value)} className={inputCls} />
                            </div>
                        </div>
                    )}

                    {/* Mode de paiement */}
                    <div>
                        <label className={labelCls}>Mode de paiement <span className="text-red-500">*</span></label>
                        <select required value={paymentMethod} onChange={e => setPaymentMethod(e.target.value)} className={inputCls}>
                            <option value="">-- Sélectionner --</option>
                            {PAYMENT_METHODS.map(m => (
                                <option key={m.value} value={m.value}>{m.label}</option>
                            ))}
                        </select>
                    </div>

                    {/* Récapitulatif montant & fin prévue */}
                    <div className="p-3 bg-gray-50 border border-gray-200 rounded-md flex items-center justify-between text-xs">
                        <span className="text-gray-500">
                            {isAbo && endDate && `Fin prévue : ${fmt(endDate)}`}
                        </span>
                        <div className="text-right">
                            <span className="text-lg font-black text-gray-900">
                                {Number(total).toLocaleString('fr-FR')} FCFA
                            </span>
                            {regFee > 0 && (
                                <span className="block text-[10px] text-gray-500">
                                    Dont {regFee.toLocaleString('fr-FR')} F d'inscription
                                </span>
                            )}
                        </div>
                    </div>

                    {error && (
                        <div className="p-3 bg-red-50 border border-red-200 text-red-700 text-xs rounded-md flex items-center gap-2">
                            <AlertCircle size={15} className="shrink-0" />
                            <span>{error}</span>
                        </div>
                    )}
                </div>

                <div className="flex items-center justify-end gap-3 px-6 py-4 bg-gray-50 border-t border-gray-100">
                    <button type="button" onClick={onClose} className="px-4 py-2 text-sm text-gray-600 hover:text-gray-800 font-semibold">
                        Annuler
                    </button>
                    <button
                        type="submit"
                        disabled={submitting}
                        className="px-5 py-2.5 bg-blue-600 hover:bg-blue-700 text-white rounded-lg text-sm font-black shadow-md flex items-center gap-2 disabled:opacity-50 cursor-pointer"
                    >
                        <CheckCircle size={16} />
                        {submitting ? 'Validation...' : 'Valider le réabonnement'}
                    </button>
                </div>
            </form>
        </div>
    );
};

export default ReabonnementModal;
